import { useEffect, useRef } from "react";
import type { OnboardingStatus } from "@/features/home/onboarding/schemas/onboarding-status.schema";
import {
  type ActivationMilestone,
  captureActivationOnce,
} from "@/shared/analytics/activation-analytics";

interface PreviousOnboardingStatus {
  workspaceId: string;
  status: OnboardingStatus;
}

export function completedActivationMilestones(
  previous: OnboardingStatus,
  current: OnboardingStatus,
): ActivationMilestone[] {
  const milestones: ActivationMilestone[] = [];
  if (!previous.steps.importTeam && current.steps.importTeam) milestones.push("team");
  if (!previous.steps.giveKudos && current.steps.giveKudos) milestones.push("kudos");
  if (!previous.steps.dailyCheckIn && current.steps.dailyCheckIn) milestones.push("check_in");
  if (!previous.complete && current.complete) milestones.push("complete");
  return milestones;
}

/** Reports only real false-to-true onboarding transitions while the app is mounted. */
export function useOnboardingAnalytics({
  userId,
  workspaceId,
  status,
}: {
  userId: string | null;
  workspaceId: string;
  status: OnboardingStatus | undefined;
}): void {
  const previousRef = useRef<PreviousOnboardingStatus | null>(null);

  useEffect(() => {
    if (!status || !userId || !status.applicable) return;
    const previous = previousRef.current;
    if (!previous || previous.workspaceId !== workspaceId) {
      previousRef.current = { workspaceId, status };
      return;
    }

    for (const milestone of completedActivationMilestones(previous.status, status)) {
      captureActivationOnce(userId, workspaceId, milestone);
    }
    previousRef.current = { workspaceId, status };
  }, [status, userId, workspaceId]);
}
